import React, { useState, useEffect } from 'react';
import { Shield, Cpu, Mail, Linkedin, ChevronRight, Check, Target, Users, Zap, Building2, Briefcase, Menu, X, Rocket } from 'lucide-react';

const RECAPTCHA_SITE_KEY = '6Lf8RVwsAAAAAGdALVZfCqkd9A65kAWedJ7qpLKw';

const HexagonMesh = React.memo(() => {
  const size = 38;
  // Pointy-top hex dimensions
  const hexW = Math.sqrt(3) * size;
  const hexH = 2 * size;
  const cols = 18;
  const rows = 10;

  // The SVG canvas
  const svgW = 1400;
  const svgH = 500;

  // Focal point: far right so the bright core doesn't overlap the text
  const focalX = svgW * 0.78;
  const focalY = svgH * 0.45;
  const fadeRadius = 380;

  // Pointy-top hex centers, offset so the grid starts on the right side only
  const hexCenters = [];
  const offsetX = svgW * 0.45;
  const offsetY = -svgH * 0.1;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cx = offsetX + col * hexW + (row % 2 === 1 ? hexW / 2 : 0);
      const cy = offsetY + row * hexH * 0.75;
      hexCenters.push({ cx, cy, row, col });
    }
  }

  const opacityAt = (x, y) => {
    const dx = x - focalX;
    const dy = y - focalY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const t = Math.min(dist / fadeRadius, 1);
    return Math.max(0, 1 - t * t);
  };

  // Pointy-top hex vertices (angle 0 = top vertex)
  const hexPoints = (cx, cy) => {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      pts.push({ x: cx + size * Math.cos(angle), y: cy + size * Math.sin(angle) });
    }
    return pts;
  };

  // Edge lines — opacity keyed to hex center so all 6 edges of a hex match
  const edgeLines = [];
  hexCenters.forEach(({ cx, cy }) => {
    const op = opacityAt(cx, cy);
    if (op < 0.02) return;
    const pts = hexPoints(cx, cy);
    for (let i = 0; i < 6; i++) {
      const p1 = pts[i];
      const p2 = pts[(i + 1) % 6];
      edgeLines.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, opacity: op });
    }
  });

  // Unique node dots
  const nodeMap = new Map();
  hexCenters.forEach(({ cx, cy }) => {
    hexPoints(cx, cy).forEach(p => {
      const key = `${Math.round(p.x * 4)},${Math.round(p.y * 4)}`;
      if (!nodeMap.has(key)) {
        nodeMap.set(key, { x: p.x, y: p.y, opacity: opacityAt(p.x, p.y) });
      }
    });
  });
  const nodes = Array.from(nodeMap.values()).filter(n => n.opacity > 0.02);

  const pulseIndices = new Set([2, 7, 14, 23, 31, 45, 58, 67, 72]);

  return (
    <>
      <style>{`
        @keyframes nodePulse {
          0%, 100% { r: 2; opacity: var(--base-op); }
          50%       { r: 3.5; opacity: calc(var(--base-op) * 1.5); }
        }
      `}</style>
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: 0 }}
        viewBox={`0 0 ${svgW} ${svgH}`}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {edgeLines.map((l, i) => (
          <line key={`e${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke="white" strokeOpacity={l.opacity * 0.22} strokeWidth="0.7" />
        ))}
        {nodes.map((n, i) => {
          const pulses = pulseIndices.has(i);
          const baseOp = n.opacity * 0.6;
          return (
            <circle key={`n${i}`} cx={n.x} cy={n.y} r={pulses ? 2.2 : 1.6}
              fill="white" fillOpacity={baseOp}
              style={pulses ? {
                animation: 'nodePulse 3s ease-in-out infinite',
                animationDelay: `${(i % 5) * 0.7}s`,
                '--base-op': baseOp
              } : {}}
            />
          );
        })}
      </svg>
    </>
  );
});

export default function StrautekWebsite() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const validateForm = () => {
    // Check required fields
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitError('Please fill in all required fields.');
      return false;
    }

    // Validate name (at least 2 characters)
    if (formData.name.trim().length < 2) {
      setSubmitError('Please enter a valid name.');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('Please enter a valid email address.');
      return false;
    }

    // Validate message length
    if (formData.message.trim().length < 10) {
      setSubmitError('Message must be at least 10 characters.');
      return false;
    }

    if (formData.message.length > 5000) {
      setSubmitError('Message is too long (maximum 5000 characters).');
      return false;
    }

    // Check for suspicious patterns (basic spam detection)
    const spamPatterns = /(viagra|cialis|casino|lottery|prize|click here|buy now)/i;
    if (spamPatterns.test(formData.message) || spamPatterns.test(formData.name)) {
      setSubmitError('Your message contains suspicious content. Please revise and try again.');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    
    // Validate form before submission
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact_form' });
      const res = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, token })
      });
      if (!res.ok) {
        const err = await res.json();
        setSubmitError(err.error || 'Submission failed. Please try again.');
        setIsSubmitting(false);
        return;
      }
      setSubmitted(true);
    } catch (e) {
      setSubmitError('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Lato, sans-serif' }}>
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-t-2" style={{ borderColor: '#1b5a7a' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button 
            onClick={() => { 
              window.scrollTo({ top: 0, behavior: 'smooth' });
              window.history.pushState('', document.title, window.location.pathname + window.location.search);
            }} 
            className="flex items-center gap-3 cursor-pointer bg-transparent border-0 p-0 outline-none"
            style={{ outline: 'none' }}
            aria-label="Scroll to top"
          >
            <img 
              src="/images/SS-Full-Logo.png" 
              alt="Strautek Solutions Logo" 
              className="h-14"
            />
          </button>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-8">
            <a href="#services" className="text-gray-600 hover:text-[#1b5a7a] transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2 rounded">Services</a>
            <a href="#about" className="text-gray-600 hover:text-[#1b5a7a] transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2 rounded">About</a>
            <a href="#contact" className="text-gray-600 hover:text-[#1b5a7a] transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2 rounded">Contact</a>
          </nav>
          
          {/* Desktop CTA Button */}
          <a 
            href="#contact" 
            className="hidden md:block px-6 py-2 rounded-lg transition-colors text-white font-medium focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2"
            style={{ backgroundColor: '#1b5a7a' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#154a63'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1b5a7a'; }}
          >
            Get Started
          </a>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-[#1b5a7a] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] rounded"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <nav className="px-6 py-4 flex flex-col gap-4">
              <a 
                href="#services" 
                className="text-gray-600 hover:text-[#1b5a7a] transition-colors font-medium py-2 focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2 rounded"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </a>
              <a 
                href="#about" 
                className="text-gray-600 hover:text-[#1b5a7a] transition-colors font-medium py-2 focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2 rounded"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </a>
              <a 
                href="#contact" 
                className="text-gray-600 hover:text-[#1b5a7a] transition-colors font-medium py-2 focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2 rounded"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </a>
              <a 
                href="#contact" 
                className="mt-2 px-6 py-2 rounded-lg text-center text-white font-medium focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2"
                style={{ backgroundColor: '#1b5a7a' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="text-white py-24 px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1b5a7a 0%, #154a63 50%, #1b5a7a 100%)' }}>
        <HexagonMesh />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-10 leading-tight md:whitespace-nowrap">
              Strategy. Trust. Technology.
            </h1>
            <p className="text-2xl md:text-3xl mb-8 leading-relaxed font-bold text-white">
              Strategic Business and Technology Consulting Services
            </p>
            <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed">
              Fractional executive leadership helping technology, cybersecurity, and services companies optimize performance, accelerate growth, and improve go-to-market execution.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href="#contact" 
                className="px-8 py-4 rounded-lg transition-all transform hover:scale-105 flex items-center gap-2 text-lg font-semibold text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1b5a7a]"
                style={{ backgroundColor: '#ffffff20', border: '2px solid white' }}
              >
                Schedule a Consultation
                <ChevronRight className="w-5 h-5" />
              </a>
              <a 
                href="#services" 
                className="bg-white/10 backdrop-blur px-8 py-4 rounded-lg hover:bg-white/20 transition-all border border-white/20 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1b5a7a]"
              >
                Explore Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive advisory and leadership services for technology, cybersecurity, and services-based organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Row 1: Fractional, GTM, Product Dev */}
            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden flex flex-col" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1b5a7a' }}>
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1b5a7a' }}>
                    Fractional Executive Leadership
                  </h3>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  Strategic leadership without full-time executive commitment.
                </p>
                <div className="border-t border-gray-200 pt-4 mt-auto">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Business Operations Leadership</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Product Management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Product Marketing</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden flex flex-col" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1b5a7a' }}>
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1b5a7a' }}>
                    Go-To-Market Strategy
                  </h3>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  Strategic planning and execution to refine messaging, launch offerings, and drive commercial expansion.
                </p>
                <div className="border-t border-gray-200 pt-4 mt-auto">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Product Messaging & Positioning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Competitive Market Analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Marketing Roadmap Development</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden flex flex-col" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1b5a7a' }}>
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1b5a7a' }}>
                    Product Management
                  </h3>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  Strategic product planning, roadmap development, and partnership strategies for market success.
                </p>
                <div className="border-t border-gray-200 pt-4 mt-auto">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Product Roadmap Planning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Partnership & Channel Strategy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Technology Roadmap Development</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Row 2: Technology, AI, Business Strategy */}
            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden flex flex-col" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1b5a7a' }}>
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1b5a7a' }}>
                    Technology & Cybersecurity
                  </h3>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  IT systems engineering and cybersecurity program development aligned with regulatory frameworks.
                </p>
                <div className="border-t border-gray-200 pt-4 mt-auto">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>IT Systems & Network Engineering</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Cybersecurity Engineering</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Cybersecurity Program Assessment</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden flex flex-col" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1b5a7a' }}>
                    <Cpu className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1b5a7a' }}>
                    AI Process Automation
                  </h3>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  Innovative solutions to build organizational efficiency, reduce response time, and improve work quality.
                </p>
                <div className="border-t border-gray-200 pt-4 mt-auto">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>AI-Based Process Automation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Business Workflow & Systems Optimization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Lead Capture & Response Systems</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden flex flex-col" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1b5a7a' }}>
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1b5a7a' }}>
                    Business Strategy
                  </h3>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  Strategic planning and management coaching using proven frameworks.
                </p>
                <div className="border-t border-gray-200 pt-4 mt-auto">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Strategic Planning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>Management Coaching</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1b5a7a' }} />
                      <span>EOS & OKR Coaching & Implementation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About/Philosophy Section */}
      <section id="about" className="py-20 px-6 scroll-mt-20" style={{ backgroundColor: '#e6e6e6' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Our Philosophy</h2>
            <p className="text-xl text-gray-700">
              Built on the foundation of strategic thinking, trusted relationships, and technological innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8">
                <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Copperplate, serif' }}>S</span>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Strategy</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Data-driven insights and proven frameworks to navigate complex business challenges and capitalize on market opportunities.
                </p>
              </div>
            </div>

            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8">
                <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Copperplate, serif' }}>T</span>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Trust</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Long-term partnerships built on transparency, integrity, and deep commitment to your success and security.
                </p>
              </div>
            </div>

            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8">
                <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Copperplate, serif' }}>T</span>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Technology</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Cutting-edge solutions that drive efficiency, enhance security, and position your organization for sustainable growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1b5a7a' }}>
              Who We Serve
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div className="font-semibold mb-2" style={{ color: '#1b5a7a' }}>IT & ICS/OT Cybersecurity</div>
                <p className="text-sm text-gray-600">Product vendors and service providers</p>
              </div>
            </div>
            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <div className="font-semibold mb-2" style={{ color: '#1b5a7a' }}>SaaS Companies</div>
                <p className="text-sm text-gray-600">Technology platforms scaling operations</p>
              </div>
            </div>
            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <div className="font-semibold mb-2" style={{ color: '#1b5a7a' }}>Private Equity</div>
                <p className="text-sm text-gray-600">Portfolio companies in need of SME and operating advisement</p>
              </div>
            </div>
            <div className="text-center bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1b5a7a' }}>
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div className="font-semibold mb-2" style={{ color: '#1b5a7a' }}>Service Organizations</div>
                <p className="text-sm text-gray-600">Non-technical businesses modernizing operations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principal Profile Section */}
      <section className="py-20 px-6" style={{ backgroundColor: '#e6e6e6' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Meet Our Principal Consultant</h2>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
            <div className="p-10 md:p-12">
              <div className="flex flex-col md:flex-row gap-10 items-start">
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="w-56 h-56 rounded-2xl overflow-hidden bg-gray-200 shadow-md">
                    <img 
                      src="/images/MKT-headshot.jpg" 
                      alt="Michael Trautman, Founder & Principal Consultant" 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-6 text-center">
                    <h3 className="text-2xl font-bold mb-1" style={{ color: '#1b5a7a' }}>Michael Trautman</h3>
                    <p className="text-gray-600 mb-4 text-sm">Founder & Principal Consultant</p>
                    <a 
                      href="https://www.linkedin.com/in/mktrautman"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg transition-colors text-white text-sm font-medium shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2"
                      style={{ backgroundColor: '#1b5a7a' }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = '#154a63'; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = '#1b5a7a'; }}
                    >
                      <Linkedin className="w-4 h-4" />
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
                
                <div className="flex-1 space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Michael Trautman is the Founder and Principal Consultant of Strautek Solutions, providing fractional executive leadership and strategic advisory services to technology, cybersecurity, and service-based businesses. His work focuses on business operations, product management, product marketing, strategic planning, and developing scalable commercial and technical execution models.
                  </p>
                  <p>
                    Prior to founding Strautek Solutions, Michael was co-founder of FoxGuard Solutions, where he served as Chief Technology Officer, Chief Security Officer, and Vice President of the cybersecurity business unit. He led corporate technology strategy, commercialization of new product and service lines, and full business unit P&L operations. Under his leadership, FoxGuard expanded into global energy, nuclear, and industrial markets and completed a strategic acquisition by Framatome in 2019.
                  </p>
                  <p>
                    In 2013, Michael led the industry-defining collaboration with the U.S. Department of Energy to create one of the first structured patch and vulnerability management programs for critical infrastructure systems. This program is now used by many of the largest electric utilities in the United States and has been adopted across manufacturing, oil and gas, transportation, and nuclear sectors.
                  </p>
                  <p>
                    Michael began his career as a solution architect, developing computing and networking solutions for the electric utility, oil and gas, and telecommunications industries. He studied Engineering Science and Mechanics at Virginia Tech and holds technical certifications from CompTIA, Microsoft, Cisco, and Fortinet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Highlight */}
      <section className="py-12 px-6 text-white" style={{ backgroundColor: '#1b5a7a' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-6">Specialized Cybersecurity Expertise</h3>
          <p className="text-lg text-blue-100 mb-8 max-w-3xl mx-auto">
            Advisory support for IT & ICS/OT cybersecurity program development including vulnerability and patch management program development and assessment.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">NERC CIP</span>
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">US DoD CMMC</span>
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">TSA Security Directives</span>
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">NIST CSF</span>
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">CIS Controls</span>
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">ISA/IEC 62443</span>
            <span className="px-4 py-2 bg-white/10 rounded-full border border-white/20">ISO/IEC 27001</span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gray-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1b5a7a' }}>Let's Talk</h2>
            <p className="text-xl text-gray-600">
              Ready to optimize performance and accelerate growth? Get in touch to discuss your needs.
            </p>
          </div>

          {submitted ? (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-green-900 mb-2">Thank you for reaching out!</h3>
              <p className="text-green-700">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ borderTop: '3px solid #1b5a7a' }}>
              <div className="p-8 md:p-10">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-2" style={{ color: '#1b5a7a' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      maxLength="100"
                      required
                      className="w-full px-4 py-3 border-2 rounded-lg outline-none transition-all"
                      style={{ borderColor: '#e6e6e6' }}
                      onFocus={(e) => { e.target.style.borderColor = '#1b5a7a'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e6e6e6'; }}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: '#1b5a7a' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      maxLength="100"
                      required
                      className="w-full px-4 py-3 border-2 rounded-lg outline-none transition-all"
                      style={{ borderColor: '#e6e6e6' }}
                      onFocus={(e) => { e.target.style.borderColor = '#1b5a7a'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e6e6e6'; }}
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="company" className="block text-sm font-semibold mb-2" style={{ color: '#1b5a7a' }}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    maxLength="100"
                    className="w-full px-4 py-3 border-2 rounded-lg outline-none transition-all"
                    style={{ borderColor: '#e6e6e6' }}
                    onFocus={(e) => { e.target.style.borderColor = '#1b5a7a'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e6e6e6'; }}
                    placeholder="Acme Corporation"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-semibold mb-2" style={{ color: '#1b5a7a' }}>
                    How can we help? *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    maxLength="5000"
                    required
                    className="w-full px-4 py-3 border-2 rounded-lg outline-none transition-all resize-none"
                    style={{ borderColor: '#e6e6e6' }}
                    onFocus={(e) => { e.target.style.borderColor = '#1b5a7a'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e6e6e6'; }}
                    placeholder="Tell us about your challenges and goals..."
                  />
                </div>

                {submitError && (
                  <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="flex justify-center">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-12 py-4 rounded-lg transition-colors font-semibold text-lg flex items-center justify-center gap-2 text-white disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#1b5a7a] focus:ring-offset-2"
                    style={{ backgroundColor: '#1b5a7a' }}
                    onMouseEnter={(e) => { if (!isSubmitting) e.target.style.backgroundColor = '#154a63'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = '#1b5a7a'; }}
                  >
                    <Mail className="w-5 h-5" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 text-center mt-4">
                  This form is protected by reCAPTCHA. <a href="https://terms.google.com" target="_blank" rel="noopener noreferrer" className="underline">Terms</a> and <a href="https://privacypolicy.google.com" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> apply.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 text-white" style={{ backgroundColor: '#1b5a7a' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-12 flex-wrap">
            <div className="flex items-center gap-3">
              <img 
                src="/images/SS-Full-Logo-Dark.png" 
                alt="Strautek Solutions" 
                className="h-14"
              />
            </div>
            
            <div className="flex gap-4 ml-auto pl-4">
              <a 
                href="https://www.linkedin.com/company/strautek/" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1b5a7a]"
              >
                <Linkedin className="w-5 h-5" style={{ color: '#0A66C2' }} />
              </a>
            </div>

            <p className="text-blue-100 text-xs mr-16">&copy; {new Date().getFullYear()} Strautek Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}